import { Skeleton } from '@mantine/core'
import { ImageWithFallback } from '@/components'
import { useMobilePropertyImg } from '../hooks/useMobilepropertyImg'
import { getPropertyDetailsImageUrl } from '@/utils/imageUtils'
import { toSlug } from '@/utils/common'

function MobilePropertyImage({ data, loading, slug }) {
  const { displayImages, hasOtherPhotos, router } =
    useMobilePropertyImg({ data })

  // Context-rich alt text for multimodal AI engines + image SEO (TNA-268).
  const propertyLabel = [data?.name, data?.city].filter(Boolean).join(' in ')
  const altFor = (n) =>
    propertyLabel ? `${propertyLabel} — photo ${n}` : `Property photo ${n}`

  const handlePhotoClick = () => {
    if (data?.property_uid) {
      router.push(`/${toSlug(data?.city)}/${slug}/show-more`)
    }
  }

  return (
    <>
      {/* The first grid image below uses `priority`, so Next.js already injects
          the correct <link rel="preload"> for the optimized LCP image. */}

      {/* Parent */}
      <div
        className='flex flex-col gap-[19px] h-full mx-auto w-full p-[15px] overflow-hidden'
        style={{ transform: 'rotate(0deg)' }}
      >
        {/* Top Row */}
        <div className='flex gap-[19px] flex-1'>
          {/* Top Left */}
          <div
            className='relative rounded-[20px] overflow-hidden flex-1 cursor-pointer'
            onClick={handlePhotoClick}
          >
            {loading || !data ? (
              <Skeleton visible={loading} height='100%' radius={16} />
            ) : hasOtherPhotos && data?.other_photos?.[0] ? (
              <div className='relative w-full aspect-square'>
                <ImageWithFallback
                  src={getPropertyDetailsImageUrl(data.other_photos[0])}
                  alt={altFor(1)}
                  fill
                  sizes='(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw'
                  className='object-cover rounded-[20px]'
                  priority
                />
              </div>
            ) : data?.main_photo ? (
              <div className='relative w-full aspect-square'>
                <ImageWithFallback
                  src={data.main_photo}
                  alt={altFor(1)}
                  fill
                  sizes='(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw'
                  className='object-cover rounded-[20px]'
                  priority
                />
              </div>
            ) : (
              <div className='w-full aspect-square bg-gray-100 flex items-center justify-center text-sm text-gray-500 rounded-[20px]'>
                Image not available
              </div>
            )}
          </div>

          {/* Top Right */}
          <div
            className='relative rounded-[20px] overflow-hidden flex-1 cursor-pointer'
            onClick={handlePhotoClick}
          >
            {loading || !data ? (
              <Skeleton visible={loading} height='100%' radius={16} />
            ) : hasOtherPhotos && data?.other_photos?.[1] ? (
              <div className='relative w-full aspect-square'>
                <ImageWithFallback
                  src={getPropertyDetailsImageUrl(data.other_photos[1])}
                  alt={altFor(2)}
                  fill
                  sizes='(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw'
                  className='object-cover rounded-[20px]'
                />
              </div>
            ) : (
              <div className='w-full aspect-square bg-gray-100 flex items-center justify-center text-sm text-gray-500 rounded-[20px]'>
                Image not available
              </div>
            )}
          </div>
        </div>

        {/* Bottom Row */}
        <div className='flex gap-[19px] flex-1'>
          {/* Bottom Left */}
          <div
            className='relative rounded-[20px] overflow-hidden flex-1 cursor-pointer'
            onClick={handlePhotoClick}
          >
            {loading || !data ? (
              <Skeleton visible={loading} height='100%' radius={16} />
            ) : hasOtherPhotos && data?.other_photos?.[2] ? (
              <div className='relative w-full aspect-square'>
                <ImageWithFallback
                  src={getPropertyDetailsImageUrl(data.other_photos[2])}
                  alt={altFor(3)}
                  fill
                  sizes='(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw'
                  className='object-cover rounded-[20px]'
                />
              </div>
            ) : (
              <div className='w-full aspect-square bg-gray-100 flex items-center justify-center text-sm text-gray-500 rounded-[20px]'>
                Image not available
              </div>
            )}
          </div>

          {/* Bottom Right with overlay */}
          <div
            className='relative rounded-[20px] overflow-hidden flex-1 cursor-pointer'
            onClick={handlePhotoClick}
          >
            {loading || !data ? (
              <Skeleton visible={loading} height='100%' radius={16} />
            ) : hasOtherPhotos && data?.other_photos?.[3] ? (
              <div className='relative w-full aspect-square'>
                <ImageWithFallback
                  src={getPropertyDetailsImageUrl(data.other_photos[3])}
                  alt={altFor(4)}
                  fill
                  sizes='(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw'
                  className='object-cover rounded-[20px]'
                />
                {displayImages?.length > 4 && (
                  <div
                    className='absolute inset-0 bg-black/50 text-white flex items-center justify-center text-center cursor-pointer rounded-[20px]'
                    onClick={handlePhotoClick}
                  >
                    <div>
                      <span className='text-lg font-medium block'>
                        Show more
                      </span>
                      <span className='text-lg font-medium'>photos</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className='w-full aspect-square bg-gray-100 flex items-center justify-center text-sm text-gray-500 rounded-[20px]'>
                {!hasOtherPhotos
                  ? 'Only main photo available'
                  : 'More photos not available'}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default MobilePropertyImage
